import type { Component } from 'vue'
import type { RouteRecordRaw, Router } from 'vue-router'

import AdminLayout from '@/layouts/AdminLayout.vue'
import AdminModulePending from '@/apps/admin/pages/AdminModulePending.vue'
import AdminRemoteFrame from '@/apps/admin/components/AdminRemoteFrame.vue'
import type { AdminMenuRouteSource } from '@/shared/types/admin'
import {
  normalizeAdminExternalUrl,
  normalizeAdminMenuPath,
  toAdminExternalFramePath
} from '@/router/admin-menu-url'

export { normalizeAdminMenuPath } from '@/router/admin-menu-url'

export type AdminViewLoader = () => Promise<{ default: Component }>
export type AdminViewModules = Record<string, AdminViewLoader | undefined>

export interface AdminRouteRegistration {
  id: string | number
  path: string | null
  status: 'registered' | 'placeholder' | 'external' | 'missing-view' | 'existing' | 'invalid-url'
}

const adminViewModules = import.meta.glob<{ default: Component }>(
  '/src/apps/admin/views/**/*.vue'
)

export const ADMIN_BOOTSTRAP_DYNAMIC_ROUTES: AdminMenuRouteSource[] = [
  {
    id: 'route-probe',
    name: 'Admin 动态路由探针',
    url: 'system/route-probe',
    titleKey: 'admin.routeProbe.title'
  },
  {
    id: 'sys-dict-data',
    name: '字典数据',
    url: 'sys/dict-data'
  },
  {
    id: 'sys-sysarea',
    name: '业务区域',
    url: 'sys/sysarea'
  }
]

export const toAdminViewKey = (menuUrl: string | null | undefined): string | null => {
  const routePath = normalizeAdminMenuPath(menuUrl)
  if (!routePath) {
    return null
  }

  return `/src/apps/admin/views/${routePath.slice('/admin/'.length)}.vue`
}

const toDynamicRouteName = (item: AdminMenuRouteSource): string => {
  const safeId = String(item.id).replace(/[^a-zA-Z0-9_-]/g, '-')
  return `admin-dynamic-${safeId}`
}

const flattenMenuRoutes = (items: AdminMenuRouteSource[]): AdminMenuRouteSource[] => {
  return items.flatMap(item => [item, ...flattenMenuRoutes(item.children || [])])
}

export const getAdminViewModules = (): AdminViewModules => adminViewModules

export const registerAdminDynamicRoutes = (
  router: Router,
  menuItems: AdminMenuRouteSource[],
  viewModules: AdminViewModules = getAdminViewModules()
): AdminRouteRegistration[] => {
  return flattenMenuRoutes(menuItems).map(item => {
    const externalUrl = normalizeAdminExternalUrl(item.url)
    if (externalUrl && item.openStyle === 1) {
      return { id: item.id, path: null, status: 'external' }
    }

    const isRemoteFrame = Boolean(externalUrl)
    const path = isRemoteFrame
      ? toAdminExternalFramePath(item.id)
      : normalizeAdminMenuPath(item.url)
    const viewKey = toAdminViewKey(item.url)

    if (!path || (!viewKey && !isRemoteFrame)) {
      return { id: item.id, path: null, status: 'invalid-url' }
    }

    const name = toDynamicRouteName(item)
    if (router.hasRoute(name) || router.getRoutes().some(route => route.path === path)) {
      return { id: item.id, path, status: 'existing' }
    }

    const viewLoader = viewKey ? viewModules[viewKey] : undefined
    const isPendingBaseView = !viewLoader && path.startsWith('/admin/base/')
    if (!viewLoader && !isPendingBaseView && !isRemoteFrame) {
      return { id: item.id, path, status: 'missing-view' }
    }

    const route: RouteRecordRaw = {
      path,
      name,
      component: AdminLayout,
      meta: {
        appScope: 'admin',
        requiresAdminAuth: true,
        dynamicAdminRoute: true,
        menuId: item.id,
        title: item.name,
        titleKey: item.titleKey,
        icon: item.icon,
        externalUrl
      },
      children: [
        {
          path: '',
          name: `${name}-view`,
          component: isRemoteFrame ? AdminRemoteFrame : viewLoader || AdminModulePending
        }
      ]
    }

    router.addRoute(route)
    return {
      id: item.id,
      path,
      status: isPendingBaseView ? 'placeholder' : 'registered'
    }
  })
}
